import { OrganizationCode } from '@quromedical/fhir-common';

import {
  type AssertRule,
  type AssertRules,
  assertError,
  composeValidator,
} from './composer';

// Task Assignment Rules
// - Only considers organizations that are assignable to a patient as a care team
// - User is the requester of the task
// - User is member of an "Assignable" Organization that is the owner of the task
// - User is the owner of the task

type OwnerType = 'practitioner' | 'organization' | 'none';

type Organization = {
  type: OrganizationCode;
  id: string;
};

export type TaskParams = {
  user: string;
  userOrganizations?: Organization[];
  requester?: string;
  ownerType: OwnerType;
  owner?: string;
};

type RuleName = 'user-is-requester' | `owner-is-${OwnerType}`;

type TaskAssertRule = AssertRule<TaskParams>;
type TaskAssertRules = AssertRules<RuleName, TaskParams>;

const getRuleName = ({ user, ownerType, requester }: TaskParams): RuleName => {
  if (user === requester) {
    return 'user-is-requester';
  }

  return `owner-is-${ownerType}` as const;
};

const assertUserIsOwner: TaskAssertRule = ({ user, owner }) => {
  if (user === owner) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ['User is not the owner of the task'],
  };
};

/**
 * Organization types that are assignable to a task that may grant a user access if they are part of
 */
export const assignableOrganizationCodes = [
  OrganizationCode.OrganizationalTeam,
  OrganizationCode.HospitalDepartment,
];

const assertUserIsInOrganization: TaskAssertRule = ({
  owner,
  userOrganizations = [],
}) => {
  if (!owner) {
    return {
      type: 'error',
      messages: ['Owner organization is not defined'],
    };
  }

  const assignableOrganizations = userOrganizations
    .filter((org) => assignableOrganizationCodes.includes(org.type))
    .map((org) => org.id);

  const ownerInAssignableOrgs = assignableOrganizations.includes(owner);

  if (ownerInAssignableOrgs) {
    return undefined;
  }

  return {
    type: 'error',
    messages: ["Task does not belong to user's organization"],
  };
};

const taskAssertions: TaskAssertRules = {
  'user-is-requester': [],
  'owner-is-practitioner': [assertUserIsOwner],
  'owner-is-organization': [assertUserIsInOrganization],
  'owner-is-none': [assertError('Task owner not found')],
};

/**
 * a function that takes in a set of parameters and based on that determines if a consumer can do a task request for the patient
 */
export const validateTaskAssignment = composeValidator({
  getRuleName,
  assertions: taskAssertions,
  defaultAssertions: [assertError('User is not assigned to task')],
});
